import { ChangeEvent, ReactNode, Ref } from "react";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { cn } from "../../../utils/cn";
import { SearchIcon } from "../../screens/S-Map/Map.icons";
import styles from "./SearchInput.module.scss";

type Props = {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
  /** Extra class applied to the outer wrapper. */
  className?: string;
  /**
   * Ref on the outer wrapper. Used by the map address search so its
   * outside-click detection covers both the input and the dropdown, and so the
   * dropdown can anchor to the wrapper.
   */
  containerRef?: Ref<HTMLDivElement>;
  /** Rendered directly below the search bar (e.g. the address autocomplete dropdown). */
  children?: ReactNode;
};

/**
 * The single search bar used across the app (branch search, address search,
 * delivery drawer, …). Matches the Figma "Search bar" spec: white, 8px radius,
 * grey border/icon/placeholder, 16px text.
 */
export const SearchInput = ({
  value,
  onChange,
  placeholder,
  testId,
  disabled,
  className,
  containerRef,
  children,
}: Props) => {
  const { t } = useTranslationI18();

  return (
    <div ref={containerRef} className={cn(styles.wrapper, className)}>
      <div className={styles.search_bar}>
        <SearchIcon size={20} className={styles.search_icon} />
        <input
          type="text"
          className={styles.search_input}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
        />
        {/* Figma: a teal "Clear" text link inside the bar, shown only once the
            customer has typed something. */}
        {!!value && !disabled && (
          <button
            type="button"
            className={styles.clear_btn}
            data-testid={testId ? `${testId}-clear` : "search-input-clear"}
            // Synthesises the same empty-value change the caller already
            // handles, so no caller needs its own reset path.
            onClick={() =>
              onChange({
                target: { value: "" },
              } as ChangeEvent<HTMLInputElement>)
            }
          >
            {t("common:clear")}
          </button>
        )}
      </div>
      {children}
    </div>
  );
};
